import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FigmaIcon from "../icons/FigmaIcon";
import { AccordionHeadingIcon } from "../job-details/StageViewAccordionList";

import InfoActionIcons from "../InfoActionIcons";
import InlineLinkEntryCard from "../PopupWindows/common/InlineLinkEntryCard";
import {
  fetchHiringManagers,
  isCompanyContactRow,
  isHiringManagerContactRow,
  normalizeHiringManagerContactKind,
  normalizeHiringManagerEntityType,
} from "../../utils/hiringManagers";
import { formatLocationLabel } from "../../utils/locationFilters";
import {
  getDefaultContactRoleLabel,
  normalizeContactRoleLabel,
} from "../../utils/contactRoles";
import {
  getDistinctJobHiringManagerName,
  getRecruiterOwnerName,
  isRecruiterContact,
} from "../../utils/recruiterOwnership";
import {
  normalizeContactEmail,
  normalizeContactImageUrl,
  normalizeContactPhone,
  normalizeContactSocialUrl,
} from "../../utils/contactChannels";
import { deriveLinkAutofill, describeLinkAutofill } from "../../utils/linkAutofill";
import {
  pillButtonFilledSx,
  pillButtonInteractiveSx,
  withPillButtonClassName,
} from "../../utils/pillButtonStyles";
import { toTimeValue } from "../../utils/dateTime";
import { resolvePublicAssetUrl } from "../../utils/apiUrls";
import { TAB_VIEW_CONTENT_WRAPPER_SX } from "../tabViewContentSpacing";
import {
  CompanyTabSectionHeader,
  TAB_BLUE,
  TAB_BORDER,
  TAB_PURPLE,
} from "./CompanyTabHeaderShared";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const TEXT = "#111827";
const MUTED = "#6B7280";
const CYAN = "#29ABFF";
const COMPANY_CONTACT_REPAIR_REFRESH_DELAY_MS = 1500;
const COMPANY_CONTACT_REPAIR_NOTICE_CLEAR_DELAY_MS = 4000;
const CONTACT_FILTER_OPTIONS = [
  { value: "all", label: "All Contacts" },
  { value: "contact", label: "Contacts" },
  { value: "hiring_manager", label: "Hiring Managers" },
];
const CONTACT_KIND_OPTIONS = [
  { value: "contact", label: "Contact" },
  { value: "hiring_manager", label: "Hiring Manager" },
];

const getContactLinkSourceLabel = (linkInfo = {}) => {
  if (linkInfo?.sourceBucket === "linkedin") return "LinkedIn";
  if (linkInfo?.sourceBucket === "indeed") return "Indeed";
  if (linkInfo?.sourceBucket === "website") return "Website";
  return "Other";
};

const buildContactFormPatchFromLink = (
  linkInfo = {},
  currentForm = {},
  fallbackSource = "Manual entry"
) => {
  const normalizedUrl = normalizeText(linkInfo?.normalizedUrl);
  if (!normalizedUrl) return {};

  const nextPatch = {};
  const nextSource = getContactLinkSourceLabel(linkInfo);
  const currentSource = normalizeText(currentForm?.source);
  const normalizedFallbackSource = normalizeText(fallbackSource, "Manual entry");

  if (!currentSource || currentSource === normalizedFallbackSource) {
    nextPatch.source = nextSource || normalizedFallbackSource;
  }

  const profileName = normalizeText(linkInfo?.profileName);
  if (profileName && !normalizeText(currentForm?.name)) {
    nextPatch.name = profileName;
  }

  if (linkInfo?.sourceBucket === "linkedin") {
    nextPatch.linkedin_url = normalizeContactSocialUrl("linkedin", normalizedUrl);
  }

  return nextPatch;
};

const shouldRequestCompanyContactRepair = (contact = {}) => {
  const hasPublicProfileUrl = Boolean(normalizeText(contact?.linkedin_url));
  if (!hasPublicProfileUrl) return false;

  return Boolean(
    !normalizeText(contact?.email) ||
      !normalizeText(contact?.phone) ||
      !normalizeText(contact?.profile_image) ||
      !normalizeText(contact?.linkedin_url)
  );
};
const getDefaultRoleForContactKind = (value) =>
  normalizeHiringManagerContactKind(value, "contact") === "hiring_manager"
    ? "Hiring Manager"
    : "Team Member";
const getPreferredContactKindForFilter = (value) =>
  value === "hiring_manager" ? "hiring_manager" : "contact";
const buildContactFormState = ({
  contact = null,
  preferredKind = "contact",
  fallbackSource = "",
} = {}) => {
  const contactKind = normalizeHiringManagerContactKind(
    contact?.contact_kind,
    preferredKind
  );
  const defaultRole = getDefaultContactRoleLabel({
    contactKind,
    source: contact?.source || fallbackSource,
    fallback: getDefaultRoleForContactKind(contactKind),
  });

  return {
    name: normalizeText(contact?.name),
    role: normalizeContactRoleLabel(contact?.role, {
      contactKind,
      source: contact?.source || fallbackSource,
      fallback: defaultRole,
    }),
    email: normalizeContactEmail(contact?.email),
    phone: normalizeContactPhone(contact?.phone),
    linkedin_url: normalizeContactSocialUrl("linkedin", contact?.linkedin_url),
    profile_image: normalizeContactImageUrl(contact?.profile_image),
    source: normalizeText(contact?.source, fallbackSource),
    contact_kind: contactKind,
  };
};

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    "";

  return token
    ? {
        Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
        "x-access-token": token.replace(/^Bearer\s+/i, ""),
      }
    : {};
};

const normalizeText = (value, fallback = "") => {
  const normalized = String(value ?? "").trim();
  return normalized || fallback;
};

// Team photos can come from a public company page/LinkedIn (absolute URL) or
// from a cached backend upload.  Upload paths must be resolved against the API
// host; otherwise `/uploads/...` is requested from the React dev server and
// the Avatar falls back to initials.
const resolveContactProfileImage = (value = "") =>
  // Contact photos are served directly from /uploads/contacts_images. Routing
  // them through /api/uploads produces a broken URL for the uploads handler.
  resolvePublicAssetUrl(value, { apiBase: API_BASE });

const toContactKey = (value) =>
  normalizeText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
const toDisplayContactKey = (contact = {}) => {
  if (contact?.id != null && contact?.id !== "") {
    return `id:${contact.id}`;
  }

  return [
    toContactKey(contact?.name),
    toContactKey(contact?.role),
    toContactKey(contact?.contact_kind),
  ].join("::");
};

const normalizeContactRow = (row = {}) => {
  const name = normalizeText(
    row.name || row.contact_name || row.contact_email || row.contact_phone
  );
  if (!name) return null;

  return {
    id: row.hiring_manager_id ?? row.contact_id ?? row.contactId ?? row.id ?? null,
    name,
    role: normalizeContactRoleLabel(
      row.role || row.title || row.position || row.contact_role || row.contact_title,
      {
        contactKind: row.contact_kind,
        source: row.source,
        fallback: getDefaultContactRoleLabel({
          contactKind: row.contact_kind,
          source: row.source,
          fallback: "Hiring Manager",
        }),
      }
    ),
    email: normalizeContactEmail(row.email || row.contact_email),
    phone: normalizeContactPhone(row.phone || row.contact_phone),
    linkedin_url: normalizeContactSocialUrl("linkedin", row.linkedin_url || row.linkedin),
    profile_image: normalizeContactImageUrl(
      row.profile_image || row.avatar_url || row.avatar || row.photo_url || row.image
    ),
    source: normalizeText(row.source),
    entity_type: normalizeText(row.entity_type),
    entity_id: row.entity_id ?? null,
    entity_name: normalizeText(row.entity_name),
    contact_kind: normalizeText(row.contact_kind, "hiring_manager"),
  };
};

const mergeUniqueContacts = (items = []) => {
  const byIdentity = new Map();

  for (const item of Array.isArray(items) ? items : []) {
    const contact = normalizeContactRow(item) || item;
    const nameKey = toContactKey(contact?.name);
    const emailKey = toContactKey(contact?.email);
    const linkedinKey = toContactKey(contact?.linkedin_url);
    // AI sources frequently disagree on URLs/emails for the same person. The
    // name is the stable identity for a company contact card, so always use it
    // first and only fall back to contact channels when a name is unavailable.
    const key = nameKey
      ? `${nameKey}::${toContactKey(contact?.entity_id)}`
      : linkedinKey || emailKey;
    if (!key) continue;

    const existing = byIdentity.get(key);
    if (!existing) {
      byIdentity.set(key, contact);
      continue;
    }

    // Keep the strongest version of a contact: a saved record with a usable
    // profile image wins over an AI duplicate that has no image or a stale URL.
    const preferred = contact?.profile_image && !existing?.profile_image ? contact : existing;
    const secondary = preferred === contact ? existing : contact;
    byIdentity.set(key, {
      ...secondary,
      ...preferred,
      id: preferred?.id ?? secondary?.id ?? null,
      profile_image: preferred?.profile_image || secondary?.profile_image || "",
      email: preferred?.email || secondary?.email || "",
      phone: preferred?.phone || secondary?.phone || "",
      linkedin_url: preferred?.linkedin_url || secondary?.linkedin_url || "",
    });
  }

  return Array.from(byIdentity.values());
};

const areContactsEqual = (left = [], right = []) => {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    const previous = left[index] || {};
    const next = right[index] || {};
    // Background enrichment commonly keeps the same contact identity while
    // adding a public headshot or other profile details. Compare those fields
    // too, otherwise React retains the initials-only card forever.
    if (
      toDisplayContactKey(previous) !== toDisplayContactKey(next) ||
      normalizeText(previous.name) !== normalizeText(next.name) ||
      normalizeText(previous.role) !== normalizeText(next.role) ||
      normalizeText(previous.profile_image) !== normalizeText(next.profile_image) ||
      normalizeText(previous.linkedin_url) !== normalizeText(next.linkedin_url) ||
      normalizeText(previous.email) !== normalizeText(next.email) ||
      normalizeText(previous.phone) !== normalizeText(next.phone)
    ) {
      return false;
    }
  }

  return true;
};

const buildContactActions = (contact = {}) => [
  { type: "linkedin", href: contact.linkedin_url },
  { type: "email", href: contact.email },
  { type: "phone", href: contact.phone },
].filter((action) => action?.href);

const normalizeJobRow = (row = {}) => {
  const title = normalizeText(
    row.job_title ||
      row.jobTitle ||
      row.position_title ||
      row.positionTitle ||
      row.role_title ||
      row.roleTitle ||
      row.job?.title
  );
  if (!title) return null;
  const recruiter = getRecruiterOwnerName(row);
  const hiringManager = getDistinctJobHiringManagerName(row);

  return {
    id: row.id ?? null,
    type: "Job",
    title,
    companyName: normalizeText(row.company_name || row.company),
    source: normalizeText(row.source || row.location || row.status, "Recent company job"),
    location: normalizeText(row.location),
    status: normalizeText(row.status),
    owner: recruiter,
    recruiter,
    hiringManager,
    contactInfo: normalizeText(row.contact_email || row.contact_phone),
    topCandidate: normalizeText(row.top_candidate),
    opportunityValue: row.opportunity_value ?? null,
    matchScore: row.match_score != null ? Number(row.match_score) : null,
    timestamp: row.created_at || row.updated_at || null,
  };
};

const normalizeLeadRow = (row = {}) => {
  const title = normalizeText(
    row.job_title ||
      row.jobTitle ||
      row.position_title ||
      row.positionTitle ||
      row.role_title ||
      row.roleTitle
  );
  if (!title) return null;
  const recruiter = getRecruiterOwnerName(row);

  return {
    id: row.id ?? null,
    type: "Lead",
    title,
    companyName: normalizeText(row.company_name || row.name),
    source: normalizeText(row.source),
    location: normalizeText(
      formatLocationLabel(row.location_city, row.location_state, row.location_country) ||
        row.location
    ),
    status: normalizeText(row.status || row.contact_title, "Related company lead"),
    owner: recruiter,
    recruiter,
    hiringManager: "",
    contactInfo: normalizeText(row.contact_email || row.contact_phone),
    topCandidate: normalizeText(row.top_candidate),
    opportunityValue: row.opportunity_value ?? null,
    matchScore: row.match_score != null ? Number(row.match_score) : null,
    timestamp: row.created_at || row.updated_at || null,
  };
};

function InfoRow({ label, value, isLink, isHighlight, onClick }) {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", gap: 0.5, lineHeight: 1.6 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: TEXT,
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        {label}:
      </Typography>
      {isLink ? (
        <Typography
          component="a"
          href={String(value).startsWith("http") ? value : `mailto:${value}`}
          sx={{
            fontSize: 12,
            color: TAB_BLUE,
            textDecoration: "none",
            wordBreak: "break-all",
          }}
        >
          {value}
        </Typography>
      ) : onClick ? (
        <Typography
          component="button"
          type="button"
          onClick={onClick}
          sx={{
            border: 0,
            background: "transparent",
            p: 0,
            fontSize: 12,
            color: TAB_BLUE,
            fontWeight: 500,
            cursor: "pointer",
            textAlign: "left",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value}
        </Typography>
      ) : isHighlight ? (
        <Typography sx={{ fontSize: 12, color: TAB_BLUE, fontWeight: 500 }}>{value}</Typography>
      ) : (
        <Typography sx={{ fontSize: 12, color: TEXT }}>{value}</Typography>
      )}
    </Box>
  );
}

export default function CompanyContactsTabContent({
  company = {},
  companyId,
  leadId,
  onAddNewJob,
  onAddNewLead,
  onContactsChange,
  onVisibleContactsCountChange,
  reloadKey = 0,
}) {
  const navigate = useNavigate();
  const resolvedCompanyEntityId = useMemo(() => {
    const numericId = Number(companyId);
    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }, [companyId]);
  const resolvedLeadEntityId = useMemo(() => {
    const rawId = leadId ?? company?.lead_id ?? company?.leadId ?? company?.entity_id ?? company?.id;
    const numericId = Number(rawId);
    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }, [company?.entity_id, company?.id, company?.lead_id, company?.leadId, leadId]);
  const resolvedCompanyId = useMemo(() => {
    return resolvedCompanyEntityId ?? resolvedLeadEntityId ?? NaN;
  }, [resolvedCompanyEntityId, resolvedLeadEntityId]);

  const relatedLeads = useMemo(
    () =>
      (Array.isArray(company?.related_leads) ? company.related_leads : []).filter((row) => {
        const rowCompanyId = Number(row?.company_id ?? row?.companyId ?? 0);
        return (
          Number.isFinite(resolvedCompanyId) &&
          resolvedCompanyId > 0 &&
          Number.isFinite(rowCompanyId) &&
          rowCompanyId === resolvedCompanyId
        );
      }),
    [company?.related_leads, resolvedCompanyId]
  );
  const companyEntityName = useMemo(
    () => normalizeText(company?.name || company?.company_name, "Company"),
    [company?.name, company?.company_name]
  );
  const companyContactRepairPending = useMemo(
    () => Boolean(company?.contact_repair_pending),
    [company?.contact_repair_pending]
  );
  const sourceContacts = useMemo(
    () =>
      mergeUniqueContacts(
        (Array.isArray(company?.contacts) ? company.contacts : []).filter(
          (contact) => !isRecruiterContact(contact)
        )
      ),
    [company?.contacts]
  );
  const hasExplicitContactsField = useMemo(
    () =>
      Boolean(
        company &&
          typeof company === "object" &&
          Object.prototype.hasOwnProperty.call(company, "contacts")
      ),
    [company]
  );
  const hasExplicitContactsCountField = useMemo(
    () =>
      Boolean(
        company &&
          typeof company === "object" &&
          (Object.prototype.hasOwnProperty.call(company, "contactsCount") ||
            Object.prototype.hasOwnProperty.call(company, "contacts_count"))
      ),
    [company]
  );
  const expectedContactCount = Number(company?.contactsCount ?? company?.contacts_count ?? 0) || 0;
  const [hydratedContacts, setHydratedContacts] = useState(() => sourceContacts);
  const hydratedContactsOwnerIdRef = useRef(resolvedCompanyEntityId);
  const initialContacts = useMemo(
    () => mergeUniqueContacts([...sourceContacts, ...hydratedContacts]),
    [hydratedContacts, sourceContacts]
  );

  const [contacts, setContacts] = useState(initialContacts);
  const [jobs, setJobs] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState("");
  const [jobsError, setJobsError] = useState("");
  const [selectedMember, setSelectedMember] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [itemsView, setItemsView] = useState("grid");
  const [recentItemsExpanded, setRecentItemsExpanded] = useState(true);
  const [selectedRecentItemKey, setSelectedRecentItemKey] = useState("");
  const [addRecentItemAnchorEl, setAddRecentItemAnchorEl] = useState(null);
  const [contactFilter, setContactFilter] = useState("all");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactDialogMode] = useState("create");
  const [contactDialogEntryMode] = useState("manual");
  const [contactForm, setContactForm] = useState(() =>
    buildContactFormState({
      preferredKind: "contact",
    })
  );
  const [contactLinkInput, setContactLinkInput] = useState("");
  const [contactLinkError, setContactLinkError] = useState("");
  const [contactActionError, setContactActionError] = useState("");
  const [contactDialogError, setContactDialogError] = useState("");
  const [contactRepairNotice, setContactRepairNotice] = useState("");
  const [contactRepairReloadKey, setContactRepairReloadKey] = useState(0);
  const [companyContactRefreshActive, setCompanyContactRefreshActive] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);
  const pendingContactRepairRefreshesRef = useRef(0);
  const contactRepairBaseCountRef = useRef(0);
  const contactRepairNoticeTimeoutRef = useRef(null);
  const shouldHydrateCompanyContacts =
    Boolean(resolvedCompanyEntityId) && expectedContactCount > 0 && !sourceContacts.length;

  useEffect(() => {
    setContacts((prev) => (areContactsEqual(prev, initialContacts) ? prev : initialContacts));
  }, [initialContacts]);

  useEffect(() => {
    if (sourceContacts.length) {
      hydratedContactsOwnerIdRef.current = resolvedCompanyEntityId;
      setHydratedContacts((prev) =>
        areContactsEqual(prev, sourceContacts) ? prev : sourceContacts
      );
      return;
    }

    if (
      resolvedCompanyEntityId &&
      hydratedContactsOwnerIdRef.current === resolvedCompanyEntityId &&
      ((!hasExplicitContactsField && !hasExplicitContactsCountField) || expectedContactCount > 0)
    ) {
      return;
    }

    hydratedContactsOwnerIdRef.current = resolvedCompanyEntityId;
    setHydratedContacts([]);
  }, [
    expectedContactCount,
    hasExplicitContactsCountField,
    hasExplicitContactsField,
    resolvedCompanyEntityId,
    sourceContacts,
  ]);

  useEffect(() => {
    if (!shouldHydrateCompanyContacts) return undefined;

    const controller = new AbortController();
    let active = true;

    fetch(`${API_BASE}/api/companies/${encodeURIComponent(String(resolvedCompanyEntityId))}`, {
      headers: {
        ...getAuthHeaders(),
      },
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            payload?.error || `Failed to hydrate company contacts (${response.status})`
          );
        }
        return payload;
      })
      .then((payload) => {
        if (!active) return;

        const refreshedContacts = mergeUniqueContacts(
          (Array.isArray(payload?.company?.contacts) ? payload.company.contacts : []).filter(
            (contact) => !isRecruiterContact(contact)
          )
        );
        const responseContactsCount = Number(
          payload?.company?.contactsCount ?? payload?.company?.contacts_count ?? NaN
        );
        const hasExplicitZeroContacts =
          Number.isFinite(responseContactsCount) && responseContactsCount === 0;

        hydratedContactsOwnerIdRef.current = resolvedCompanyEntityId;
        setHydratedContacts((prev) =>
          areContactsEqual(prev, refreshedContacts) ? prev : refreshedContacts
        );
        if (refreshedContacts.length || hasExplicitZeroContacts) {
          onContactsChange?.(refreshedContacts);
        }
      })
      .catch((error) => {
        if (!active || error?.name === "AbortError") return;
        console.error("Failed to hydrate company contacts:", error);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [onContactsChange, resolvedCompanyEntityId, shouldHydrateCompanyContacts]);

  useEffect(() => {
    if (!reloadKey || !resolvedCompanyEntityId) return undefined;

    const controller = new AbortController();
    let active = true;

    fetch(`${API_BASE}/api/companies/${encodeURIComponent(String(resolvedCompanyEntityId))}`, {
      headers: {
        ...getAuthHeaders(),
      },
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to refresh company contacts (${response.status})`);
        }
        return payload;
      })
      .then((payload) => {
        if (!active) return;

        const refreshedContacts = mergeUniqueContacts(
          (Array.isArray(payload?.company?.contacts) ? payload.company.contacts : []).filter(
            (contact) => !isRecruiterContact(contact)
          )
        );
        setHydratedContacts((prev) =>
          areContactsEqual(prev, refreshedContacts) ? prev : refreshedContacts
        );
        setContacts((prev) =>
          areContactsEqual(prev, refreshedContacts) ? prev : refreshedContacts
        );
        // Report an empty list too: a server-confirmed zero must clear a stale badge.
        onContactsChange?.(refreshedContacts);
      })
      .catch((error) => {
        if (!active || error?.name === "AbortError") return;
        console.error("Failed to refresh contacts after background enrichment:", error);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [onContactsChange, reloadKey, resolvedCompanyEntityId]);

  useEffect(() => {
    pendingContactRepairRefreshesRef.current = 0;
    contactRepairBaseCountRef.current = 0;
    setCompanyContactRefreshActive(false);
    setContactRepairNotice("");
    setContactRepairReloadKey(0);
    if (contactRepairNoticeTimeoutRef.current) {
      window.clearTimeout(contactRepairNoticeTimeoutRef.current);
      contactRepairNoticeTimeoutRef.current = null;
    }
  }, [resolvedCompanyEntityId]);

  useEffect(
    () => () => {
      if (contactRepairNoticeTimeoutRef.current) {
        window.clearTimeout(contactRepairNoticeTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!contactRepairReloadKey || !resolvedCompanyEntityId) return undefined;

    const controller = new AbortController();
    let active = true;
    let pendingTimeoutId = null;

    fetch(`${API_BASE}/api/companies/${encodeURIComponent(String(resolvedCompanyEntityId))}`, {
      headers: {
        ...getAuthHeaders(),
      },
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            payload?.error || `Failed to refresh company contacts (${response.status})`
          );
        }
        return payload;
      })
      .then((payload) => {
        if (!active) return;

        const refreshedContacts = mergeUniqueContacts(
          (Array.isArray(payload?.company?.contacts) ? payload.company.contacts : []).filter(
            (contact) => !isRecruiterContact(contact)
          )
        );

        if (refreshedContacts.length) {
          let contactsChanged = false;
          setContacts((prev) => {
            if (areContactsEqual(prev, refreshedContacts)) {
              return prev;
            }
            contactsChanged = true;
            return refreshedContacts;
          });
          if (contactsChanged) {
            onContactsChange?.(refreshedContacts);
          }
        }

        const contactRepairPending = Boolean(payload?.contact_repair_pending);
        if (contactRepairPending) {
          const baseCount = Number(contactRepairBaseCountRef.current || 0);
          const addedCount = Math.max(0, refreshedContacts.length - baseCount);
          setCompanyContactRefreshActive(true);
          setContactRepairNotice(
            refreshedContacts.length
              ? addedCount > 0
                ? `Getting team/leadership contacts... ${refreshedContacts.length} visible so far (${addedCount} new).`
                : `Getting team/leadership contacts... ${refreshedContacts.length} visible so far.`
              : "Getting team/leadership contacts..."
          );
          pendingContactRepairRefreshesRef.current = 1;
          pendingTimeoutId = window.setTimeout(() => {
            if (!active) return;
            setContactRepairReloadKey((prev) => prev + 1);
          }, COMPANY_CONTACT_REPAIR_REFRESH_DELAY_MS);
          return;
        }

        pendingContactRepairRefreshesRef.current = 0;
        setCompanyContactRefreshActive(false);
        if (contactRepairNoticeTimeoutRef.current) {
          window.clearTimeout(contactRepairNoticeTimeoutRef.current);
          contactRepairNoticeTimeoutRef.current = null;
        }
        const baseCount = Number(contactRepairBaseCountRef.current || 0);
        const addedCount = Math.max(0, refreshedContacts.length - baseCount);
        contactRepairBaseCountRef.current = 0;
        if (refreshedContacts.length) {
          setContactRepairNotice(
            addedCount > 0
              ? `Added ${addedCount} team/leadership contact${addedCount === 1 ? "" : "s"} without reloading the page.`
              : `${refreshedContacts.length} team/leadership contact${refreshedContacts.length === 1 ? "" : "s"} loaded.`
          );
          contactRepairNoticeTimeoutRef.current = window.setTimeout(() => {
            setContactRepairNotice("");
            contactRepairNoticeTimeoutRef.current = null;
          }, COMPANY_CONTACT_REPAIR_NOTICE_CLEAR_DELAY_MS);
        } else {
          setContactRepairNotice("");
        }
      })
      .catch((error) => {
        if (!active || error?.name === "AbortError") return;
        console.error("Failed to refresh background company contact enrichment:", error);
        pendingContactRepairRefreshesRef.current = 0;
        setCompanyContactRefreshActive(false);
        contactRepairBaseCountRef.current = 0;
        setContactRepairNotice("");
      });

    return () => {
      active = false;
      if (pendingTimeoutId) {
        window.clearTimeout(pendingTimeoutId);
      }
      controller.abort();
    };
  }, [contactRepairReloadKey, onContactsChange, resolvedCompanyEntityId]);

  useEffect(() => {
    const leadIds = relatedLeads
      .map((lead) => Number(lead.id))
      .filter((id) => Number.isFinite(id) && id > 0);

    const controller = new AbortController();
    let active = true;

    setContactsLoading(true);
    setContactsError("");

    const companyHiringManagersPromise =
      Number.isFinite(resolvedCompanyId) && resolvedCompanyId > 0
        ? fetchHiringManagers({
            apiBase: API_BASE,
            entityType: "company",
            entityId: resolvedCompanyId,
            signal: controller.signal,
          }).catch(() => [])
        : Promise.resolve([]);

    Promise.all([
      companyHiringManagersPromise,
      ...leadIds.map((id) =>
        fetchHiringManagers({
          apiBase: API_BASE,
          entityType: "lead",
          entityId: id,
          signal: controller.signal,
        }).catch(() => [])
      ),
    ])
      .then(([companyHiringManagerRows, ...leadResults]) => {
        if (!active) return;

        const companyHiringManagers = (Array.isArray(companyHiringManagerRows)
          ? companyHiringManagerRows
          : []
        )
          .map((row) =>
            normalizeContactRow({
              ...row,
              entity_type: "company",
              entity_id: resolvedCompanyId,
              entity_name: companyEntityName,
              contact_kind: row?.contact_kind || "company_hiring_manager",
            })
          )
          .filter(Boolean);

        const leadHiringManagers = leadResults
          .flat()
          .map((row) =>
            normalizeContactRow({
              ...row,
              contact_kind: row?.contact_kind || "lead_hiring_manager",
            })
          )
          .filter(Boolean);

        const combined = mergeUniqueContacts([
          ...initialContacts,
          ...companyHiringManagers,
          ...leadHiringManagers,
        ]);

        if (!combined.length && shouldHydrateCompanyContacts) {
          return;
        }

        setContacts(combined);
        onContactsChange?.(combined);
      })
      .catch((error) => {
        if (!active || error?.name === "AbortError") return;
        console.error("Failed to load contacts:", error);
        setContactsError("Unable to load the latest contacts.");
        setContacts((prev) => (prev.length ? prev : initialContacts));
        if (initialContacts.length || !shouldHydrateCompanyContacts) {
          onContactsChange?.(initialContacts);
        }
      })
      .finally(() => {
        if (active) setContactsLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    companyEntityName,
    initialContacts,
    onContactsChange,
    relatedLeads,
    resolvedCompanyId,
    shouldHydrateCompanyContacts,
  ]);

  useEffect(() => {
    if (!Number.isFinite(resolvedCompanyId) || resolvedCompanyId <= 0) {
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setJobsError("");

    fetch(
      `${API_BASE}/api/jobs?page=1&limit=100&sort=created_at&order=desc&companyId=${encodeURIComponent(
        String(resolvedCompanyId)
      )}`,
      {
        headers: { ...getAuthHeaders() },
        credentials: "include",
        signal: controller.signal,
      }
    )
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to load company jobs (${response.status})`);
        }
        return payload;
      })
      .then((payload) => {
        if (!active) return;
        const nextJobs = (Array.isArray(payload?.rows) ? payload.rows : [])
          .filter((row) => Number(row?.company_id ?? row?.companyId ?? 0) === resolvedCompanyId)
          .map((row) => normalizeJobRow(row))
          .filter(Boolean);
        setJobs(nextJobs);
      })
      .catch((error) => {
        if (!active || error?.name === "AbortError") return;
        console.error("Failed to load company jobs:", error);
        setJobsError("Unable to load recent company jobs.");
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [resolvedCompanyId]);

  const recentCompanyItems = useMemo(() => {
    const leadItems = relatedLeads.map((row) => normalizeLeadRow(row)).filter(Boolean);

    return [...jobs, ...leadItems]
      .sort((left, right) => {
        const leftTime = toTimeValue(left?.timestamp);
        const rightTime = toTimeValue(right?.timestamp);
        return rightTime - leftTime;
      })
      .slice(0, 12);
  }, [relatedLeads, jobs]);

  const displayContacts = useMemo(() => {
    const seen = new Set();
    const result = [];

    for (const contact of contacts) {
      if (isRecruiterContact(contact)) continue;
      const key = toDisplayContactKey(contact);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      result.push(contact);
    }

    return result;
  }, [contacts]);

  const visibleContacts = useMemo(() => {
    if (contactFilter === "all") return displayContacts;
    if (contactFilter === "contact") {
      return displayContacts.filter((contact) => isCompanyContactRow(contact));
    }
    if (contactFilter === "hiring_manager") {
      return displayContacts.filter((contact) => isHiringManagerContactRow(contact));
    }
    return displayContacts;
  }, [contactFilter, displayContacts]);

  useEffect(() => {
    onVisibleContactsCountChange?.(visibleContacts.length);
  }, [onVisibleContactsCountChange, visibleContacts.length]);
  const companyContactRefreshBusy =
    companyContactRefreshActive ||
    (companyContactRepairPending && Boolean(pendingContactRepairRefreshesRef.current));
  const shouldShowPendingCompanyContactsState =
    !visibleContacts.length && (companyContactRefreshBusy || Boolean(contactRepairNotice));
  const selectedVisibleContact = useMemo(
    () =>
      visibleContacts.find((contact) => toDisplayContactKey(contact) === selectedContact) || null,
    [selectedContact, visibleContacts]
  );

  const memberNames = useMemo(
    () => Array.from(new Set(recentCompanyItems.map((item) => item.recruiter).filter(Boolean))),
    [recentCompanyItems]
  );

  useEffect(() => {
    if (!memberNames.length) {
      setSelectedMember("");
      return;
    }

    if (selectedMember && !memberNames.includes(selectedMember)) {
      setSelectedMember("");
    }
  }, [memberNames, selectedMember]);

  useEffect(() => {
    if (selectedContact && !selectedVisibleContact) {
      setSelectedContact(null);
    }
  }, [selectedContact, selectedVisibleContact]);

  const filteredItems = useMemo(() => {
    if (!selectedMember) return recentCompanyItems;
    const query = selectedMember.toLowerCase();
    return recentCompanyItems.filter(
      (item) => item.recruiter && item.recruiter.toLowerCase() === query
    );
  }, [recentCompanyItems, selectedMember]);

  const openRecentCompanyItem = useCallback(
    (item) => {
      const itemId = Number(item?.id || 0);
      if (!Number.isFinite(itemId) || itemId <= 0) return;

      if (item?.type === "Job") {
        const companyQuery =
          Number.isFinite(resolvedCompanyId) && resolvedCompanyId > 0
            ? `&companyId=${encodeURIComponent(String(resolvedCompanyId))}`
            : "";
        navigate(`/jobs?jobId=${encodeURIComponent(String(itemId))}&tab=details${companyQuery}`);
        return;
      }

      navigate(`/leads?leadId=${encodeURIComponent(String(itemId))}&tab=details`);
    },
    [navigate, resolvedCompanyId]
  );
  const getRecentItemKey = useCallback(
    (item) => `${String(item?.type || "item").toLowerCase()}:${String(item?.id || "")}`,
    []
  );
  const selectedRecentItem = useMemo(
    () =>
      filteredItems.find((item) => getRecentItemKey(item) === selectedRecentItemKey) || null,
    [filteredItems, getRecentItemKey, selectedRecentItemKey]
  );
  const handleEditRecentItem = useCallback(() => {
    if (!selectedRecentItem) {
      setJobsError("Select a job or lead to edit.");
      return;
    }
    setJobsError("");
    openRecentCompanyItem(selectedRecentItem);
  }, [openRecentCompanyItem, selectedRecentItem]);

  const avatarBackgrounds = [
    "linear-gradient(135deg,#a855f7,#6366f1)",
    "linear-gradient(135deg,#3b82f6,#06b6d4)",
    "linear-gradient(135deg,#f59e0b,#ef4444)",
    "linear-gradient(135deg,#10b981,#3451F4)",
    "linear-gradient(135deg,#ec4899,#8b5cf6)",
  ];

  const filterOpen = Boolean(filterAnchorEl);
  const contactSourceFallback = normalizeText(company?.source, "Manual entry");
  const contactLinkAutofill = useMemo(
    () => deriveLinkAutofill(contactLinkInput, "contact"),
    [contactLinkInput]
  );
  const contactLinkSummary = useMemo(() => {
    const summary = describeLinkAutofill(contactLinkAutofill, {
      includeProfile: true,
    });
    return summary || "Paste a public LinkedIn profile to prefill this contact.";
  }, [contactLinkAutofill]);

  const resolveManagedContactEntity = (contact = null) => {
    const explicitEntityType = normalizeHiringManagerEntityType(contact?.entity_type);
    const explicitEntityId = Number(contact?.entity_id);

    if (
      explicitEntityType &&
      Number.isFinite(explicitEntityId) &&
      explicitEntityId > 0
    ) {
      return {
        entityType: explicitEntityType,
        entityId: explicitEntityId,
      };
    }

    if (resolvedCompanyEntityId) {
      return {
        entityType: "company",
        entityId: resolvedCompanyEntityId,
      };
    }

    if (resolvedLeadEntityId) {
      return {
        entityType: "lead",
        entityId: resolvedLeadEntityId,
      };
    }

    return {
      entityType: "",
      entityId: null,
    };
  };

  const openContactView = (contact, contactKey) => {
    const contactId = contact?.id ?? contact?.hiring_manager_id ?? contact?.contact_id;
    if (contactId != null && contactId !== "") {
      // The company profile remains mounted across its tab changes. A React
      // history-only transition can therefore leave that profile on screen
      // even though the URL changes. Reload the saved contact route so the
      // Contact page is mounted reliably.
      window.location.assign(`/contacts/${encodeURIComponent(String(contactId))}`);
      return;
    }

    const { entityType, entityId } = resolveManagedContactEntity(contact);
    // AI-discovered contacts may not be stored in hiring_managers yet. They
    // should still open immediately instead of behaving like a selectable
    // card. The contact page receives the complete card data as a preview and
    // can be saved from there when the user edits it.
    const previewContact = {
      ...contact,
      entity_type: contact?.entity_type || entityType,
      entity_id: contact?.entity_id || entityId,
      entity_name: contact?.entity_name || companyEntityName,
    };
    setContactActionError("");
    navigate(`/contacts/preview-${encodeURIComponent(contactKey)}`, {
      state: { contact: previewContact, companyName: companyEntityName, isContactPreview: true },
    });
  };

  const closeContactDialog = () => {
    if (contactSaving) return;
    setContactDialogOpen(false);
    setContactLinkError("");
    setContactDialogError("");
  };

  const handleContactFormChange = (field) => (event) => {
    const nextValue = event?.target?.value ?? "";

    if (field === "contact_kind") {
      setContactForm((prev) => {
        const nextKind = normalizeHiringManagerContactKind(
          nextValue,
          getPreferredContactKindForFilter(contactFilter)
        );
        const previousDefaultRole = getDefaultRoleForContactKind(prev.contact_kind);
        const nextDefaultRole = getDefaultRoleForContactKind(nextKind);
        const currentRole = normalizeText(prev.role);
        const shouldResetRole =
          !currentRole || currentRole === previousDefaultRole;

        return {
          ...prev,
          contact_kind: nextKind,
          role: shouldResetRole ? nextDefaultRole : prev.role,
        };
      });
      return;
    }

    setContactForm((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
  };

  const handleContactLinkChange = (event) => {
    setContactLinkInput(event?.target?.value ?? "");
    setContactLinkError("");
    setContactDialogError("");
  };

  const handleApplyContactLink = () => {
    if (contactLinkAutofill?.sourceBucket !== "linkedin") {
      setContactLinkError("Enter a valid public LinkedIn profile URL.");
      return;
    }

    const nextPatch = buildContactFormPatchFromLink(
      contactLinkAutofill,
      contactForm,
      contactSourceFallback
    );

    if (!normalizeText(contactLinkAutofill?.normalizedUrl)) {
      setContactLinkError("Enter a valid public LinkedIn profile URL.");
      return;
    }

    if (!Object.keys(nextPatch).length) {
      setContactLinkError("Paste a public LinkedIn profile URL to prefill this contact.");
      return;
    }

    setContactForm((prev) => ({
      ...prev,
      ...nextPatch,
    }));
    setContactLinkError("");
    setContactDialogError("");
  };

  const handleSaveContact = async () => {
    const name = normalizeText(contactForm.name);
    const contactKind = normalizeHiringManagerContactKind(
      contactForm.contact_kind,
      getPreferredContactKindForFilter(contactFilter)
    );
    const roleValue = normalizeText(
      contactForm.role,
      getDefaultRoleForContactKind(contactKind)
    );

    if (!name) {
      setContactDialogError("Contact name is required.");
      return;
    }

    const editingContact =
      contactDialogMode === "edit" ? selectedVisibleContact : null;

    if (
      contactDialogMode === "edit" &&
      (editingContact?.id == null || editingContact?.id === "")
    ) {
      setContactDialogError("Only saved contacts can be edited from this page.");
      return;
    }

    const { entityType, entityId } = resolveManagedContactEntity(editingContact);
    if (contactDialogMode === "create" && (!entityType || !entityId)) {
      setContactDialogError("Unable to determine where to save this contact.");
      return;
    }

    setContactSaving(true);
    setContactDialogError("");

    try {
      const body = {
        name,
        role: roleValue,
        email: normalizeContactEmail(contactForm.email),
        phone: normalizeContactPhone(contactForm.phone),
        linkedin_url: normalizeContactSocialUrl("linkedin", contactForm.linkedin_url),
        profile_image: normalizeContactImageUrl(contactForm.profile_image),
        source: normalizeText(contactForm.source, contactSourceFallback),
        contact_kind: contactKind,
      };

      const isEditing = contactDialogMode === "edit";
      const url = isEditing
        ? `${API_BASE}/api/hiring-managers/${encodeURIComponent(
            String(editingContact.id)
          )}`
        : `${API_BASE}/api/hiring-managers`;

      const response = await fetch(url, {
        method: isEditing ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify(
          isEditing
            ? body
            : {
                ...body,
                entityType,
                entityId,
              }
        ),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          payload?.error ||
            payload?.message ||
            `Failed to ${isEditing ? "update" : "create"} contact (${response.status})`
        );
      }

      const savedContact =
        normalizeContactRow(payload?.hiringManager || payload?.row || payload) ||
        normalizeContactRow({
          id: isEditing ? editingContact?.id : null,
          ...body,
          entity_type: entityType || editingContact?.entity_type,
          entity_id: entityId || editingContact?.entity_id,
          entity_name: companyEntityName,
        });

      if (!savedContact) {
        throw new Error("The saved contact response could not be read.");
      }

      const selectedKey = toDisplayContactKey(editingContact);
      const nextContacts = mergeUniqueContacts(
        isEditing
          ? [
              savedContact,
              ...contacts.filter((contact) => {
                if (
                  savedContact?.id != null &&
                  contact?.id != null &&
                  String(contact.id) === String(savedContact.id)
                ) {
                  return false;
                }
                return toDisplayContactKey(contact) !== selectedKey;
              }),
            ]
          : [savedContact, ...contacts]
      );

      setContacts(nextContacts);
      setSelectedContact(toDisplayContactKey(savedContact));
      onContactsChange?.(nextContacts);
      setContactDialogOpen(false);
      setContactLinkInput("");
      setContactLinkError("");
      setContactActionError("");
      setContactDialogError("");

      if (
        Number.isFinite(resolvedCompanyEntityId) &&
        resolvedCompanyEntityId > 0 &&
        shouldRequestCompanyContactRepair(savedContact)
      ) {
        pendingContactRepairRefreshesRef.current = 0;
        setContactRepairNotice(
          "AI is searching the web for this contact's image, email, phone, and LinkedIn in the background."
        );
        setContactRepairReloadKey((prev) => prev + 1);
      } else {
        setContactRepairNotice("");
      }
    } catch (error) {
      setContactDialogError(
        error?.message ||
          `Failed to ${contactDialogMode === "edit" ? "update" : "create"} contact.`
      );
    } finally {
      setContactSaving(false);
    }
  };

  const formatPostedOn = (value) => {
    if (!value) return "";
    const timestamp = toTimeValue(value);
    if (!timestamp) return "";
    const diffMs = Date.now() - timestamp;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 day ago";
    if (diffDays < 30) return `${diffDays} days ago`;
    const diffMonths = Math.floor(diffDays / 30);
    return diffMonths === 1 ? "1 month ago" : `${diffMonths} months ago`;
  };

  return (
    <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}>
      <CompanyTabSectionHeader
        title="Contacts"
        actions={[]}
      />

      {companyContactRefreshBusy ? (
        <Box sx={{ mb: 1.75 }}>
          <LinearProgress
            sx={{
              height: 6,
              borderRadius: 999,
              backgroundColor: "rgba(156, 79, 255, 0.12)",
              "& .MuiLinearProgress-bar": {
                backgroundColor: TAB_PURPLE,
              },
            }}
          />
        </Box>
      ) : null}

      {contactActionError && !contactDialogOpen ? (
        <Typography sx={{ mb: 1.5, fontSize: 12, color: "#B45309" }}>
          {contactActionError}
        </Typography>
      ) : null}

      {contactRepairNotice && visibleContacts.length ? (
        <Typography sx={{ mb: 1.5, fontSize: 12, color: TAB_PURPLE }}>
          {contactRepairNotice}
        </Typography>
      ) : null}

      {contactsError ? (
        <Typography sx={{ mb: 1.5, fontSize: 12, color: "#B45309" }}>
          {contactsError}
        </Typography>
      ) : null}

      {contactsLoading && !visibleContacts.length ? (
        <Box
          sx={{
            py: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <CircularProgress size={24} />
          {shouldShowPendingCompanyContactsState ? (
            <Typography sx={{ fontSize: 14, color: TAB_PURPLE, textAlign: "center" }}>
              {contactRepairNotice || "Getting team/leadership contacts..."}
            </Typography>
          ) : null}
        </Box>
      ) : visibleContacts.length ? (
        <Box
          sx={{
            display: "grid",
            gap: 1.75,
            gridTemplateColumns: {
              xs: "repeat(1, minmax(0, 1fr))",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
          }}
        >
          {visibleContacts.map((contact, index) => {
            const contactKey = toDisplayContactKey(contact) || `contact-${index}`;
            const isSelected = selectedContact === contactKey;

            return (
              <Card
                key={contactKey}
                onClick={() => openContactView(contact, contactKey)}
                sx={{
                  borderRadius: "4px",
                  border: `1px solid ${isSelected ? "#8EC5FF" : TAB_BORDER}`,
                  boxShadow: "none",
                  cursor: "pointer",
                  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                  "&:hover": {
                    borderColor: "#8EC5FF",
                    boxShadow: "0 4px 14px rgba(52,81,244,0.08)",
                  },
                }}
              >
                <CardContent
                  sx={{
                    py: 2,
                    px: 1.75,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minHeight: 132,
                  }}
                >
                  <Avatar
                    src={resolveContactProfileImage(contact.profile_image) || undefined}
                    alt={`${contact.name} profile`}
                    imgProps={{ referrerPolicy: "no-referrer" }}
                    sx={{
                      width: 42,
                      height: 42,
                      fontSize: 17,
                      fontWeight: 700,
                      background: avatarBackgrounds[index % avatarBackgrounds.length],
                    }}
                  >
                    {contact.name.slice(0, 1).toUpperCase()}
                  </Avatar>

                  <Typography
                    sx={{
                      mt: 1.1,
                      fontSize: 13,
                      fontWeight: 500,
                      lineHeight: 1.25,
                      color: TEXT,
                      textAlign: "center",
                    }}
                  >
                    {contact.name}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.35,
                      minHeight: 18,
                      fontSize: 11,
                      lineHeight: 1.3,
                      color: "#98A2B3",
                      textAlign: "center",
                    }}
                  >
                    {contact.role}
                  </Typography>

                  <Box sx={{ mt: 1.1 }}>
                    <InfoActionIcons
                      actions={buildContactActions(contact)}
                      gap={0.8}
                      iconSize={20}
                      showDisabled={true}
                      showGroupDivider={false}
                    />
                  </Box>

                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: 11,
                      lineHeight: 1.25,
                      color: TAB_PURPLE,
                      textAlign: "center",
                    }}
                  >
                    {contact.entity_name || "Jobs or Leads"}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      ) : shouldShowPendingCompanyContactsState ? (
        <Box
          sx={{
            py: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <CircularProgress size={20} />
          <Typography sx={{ fontSize: 14, color: TAB_PURPLE, textAlign: "center" }}>
            {contactRepairNotice || "Getting team/leadership contacts..."}
          </Typography>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 14, color: MUTED }}>
          {contactFilter === "hiring_manager"
            ? "No hiring managers are linked to this company yet."
            : "No contacts are linked to this company yet."}
        </Typography>
      )}

      <Box mt={4}>
        <Box sx={{ borderTop: "1px solid #E5E7EB", display: "flex", alignItems: "center", minHeight: 44, px: 1.5, gap: 1 }}>
          <Box component="button" type="button" aria-expanded={recentItemsExpanded}
            aria-controls="company-recent-items" onClick={() => setRecentItemsExpanded(value => !value)}
            sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0, border: 0, background: "transparent", p: 0, textAlign: "left", cursor: "pointer", color: TAB_BLUE }}>
            <AccordionHeadingIcon stage="New" />
            <Typography component="span" sx={{ fontSize: 16, lineHeight: "24px", fontWeight: 700, color: TAB_BLUE }}>
              Last Jobs or Leads by Member ({filteredItems.length})
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.25}>
            {["grid", "list"].map(view => (
              <IconButton key={view} size="small" aria-label={`${view === "grid" ? "Grid" : "List"} view`}
                aria-pressed={itemsView === view} onClick={() => setItemsView(view)}
                sx={{ width: 30, height: 30, p: 0, color: TAB_PURPLE, backgroundColor: itemsView === view ? "rgba(124,58,237,0.10)" : "transparent", "&:hover": { backgroundColor: "rgba(124,58,237,0.08)" } }}>
                <FigmaIcon name={view} size={18} color={TAB_PURPLE} />
              </IconButton>
            ))}
          </Stack>
          <Box sx={{ height: 18, borderLeft: "1px solid #E5E7EB" }} />
          <IconButton size="small" aria-label={recentItemsExpanded ? "Collapse recent jobs or leads" : "Expand recent jobs or leads"}
            aria-expanded={recentItemsExpanded} aria-controls="company-recent-items"
            onClick={() => setRecentItemsExpanded(value => !value)}
            sx={{ width: 30, height: 30, color: TAB_PURPLE, transform: recentItemsExpanded ? "none" : "rotate(180deg)", transition: "transform 160ms ease" }}>
            <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Collapse in={recentItemsExpanded}>
          <Box id="company-recent-items" sx={{ px: 1.5, pt: 1, pb: 1.5 }}>
            <Menu
              anchorEl={addRecentItemAnchorEl}
              open={Boolean(addRecentItemAnchorEl)}
              onClose={() => setAddRecentItemAnchorEl(null)}
            >
              <MenuItem
                onClick={() => {
                  setAddRecentItemAnchorEl(null);
                  onAddNewLead?.();
                }}
              >
                Add Lead
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAddRecentItemAnchorEl(null);
                  onAddNewJob?.();
                }}
              >
                Add Job
              </MenuItem>
            </Menu>

            <Box
              sx={{
                mb: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.8}>
                <Typography sx={{ fontSize: 13, color: MUTED, fontWeight: 500 }}>By:</Typography>
                {memberNames.length ? (
                  <Select
                    value={selectedMember}
                    displayEmpty
                    inputProps={{ "aria-label": "Filter jobs or leads by member" }}
                    onChange={(event) => setSelectedMember(event.target.value)}
                    size="small"
                    variant="standard"
                    disableUnderline
                    sx={{
                      minWidth: 140,
                      fontSize: 13,
                      fontWeight: 500,
                      color: TAB_BLUE,
                      "& .MuiSelect-select": { py: 0, pr: "24px !important" },
                    }}
                  >
                    <MenuItem value="">All members</MenuItem>
                    {memberNames.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: TAB_BLUE }}>
                    All members
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" onClick={event => setAddRecentItemAnchorEl(event.currentTarget)} sx={{ color: TAB_PURPLE, minWidth: 0 }}>Add New</Button>
                <Box sx={{ height: 14, borderLeft: "1px solid #D0D5DD" }} />
                <Button size="small" onClick={handleEditRecentItem} sx={{ color: TAB_PURPLE, minWidth: 0 }}>Edit</Button>
              </Stack>
            </Box>

            {jobsError ? (
              <Typography sx={{ mb: 1.5, fontSize: 12, color: "#B45309" }}>
                {jobsError}
              </Typography>
            ) : null}

            {filteredItems.length ? (
              <Box
                sx={
                  itemsView === "grid"
                    ? {
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: {
                          xs: "repeat(1, minmax(0, 1fr))",
                          sm: "repeat(2, minmax(0, 1fr))",
                          md: "repeat(3, minmax(0, 1fr))",
                        },
                      }
                    : { display: "flex", flexDirection: "column", gap: 1.5 }
                }
              >
                {filteredItems.map((item, index) => (
                  <Card
                    key={`${item.type}-${item.id ?? item.title}-${index}`}
                    onClick={() => {
                      setJobsError("");
                      setSelectedRecentItemKey((current) => {
                        const itemKey = getRecentItemKey(item);
                        return current === itemKey ? "" : itemKey;
                      });
                    }}
                    sx={{
                      borderRadius: "4px",
                      border: `1px solid ${
                        selectedRecentItemKey === getRecentItemKey(item) ? TAB_PURPLE : TAB_BORDER
                      }`,
                      boxShadow: "none",
                      cursor: "pointer",
                    }}
                  >
                    <CardContent sx={{ p: 1.9 }}>
                      <Box
                        sx={{
                          mb: 1.35,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: CYAN }}>
                          Match Score:{" "}
                          <Box component="span" sx={{ color: CYAN }}>
                            {item.matchScore != null ? `${item.matchScore}%` : "--"}
                          </Box>
                        </Typography>
                        {selectedRecentItemKey === getRecentItemKey(item) ? (
                          <CheckBoxIcon sx={{ fontSize: 16, color: TAB_PURPLE }} />
                        ) : (
                          <CheckBoxOutlineBlankIcon sx={{ fontSize: 16, color: "#D0D5DD" }} />
                        )}
                      </Box>

                      <Stack spacing={itemsView === "grid" ? 0.22 : 0}
                        sx={itemsView === "list" ? { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1 } : undefined}>
                        <InfoRow label="Company Name" value={item.companyName} isHighlight />
                        <InfoRow
                          label="Job"
                          value={item.title}
                          isHighlight
                          onClick={
                            Number(item?.id) > 0
                              ? () => openRecentCompanyItem(item)
                              : undefined
                          }
                        />
                        <InfoRow label="Source" value={item.source} />
                        <InfoRow label="Posted On" value={formatPostedOn(item.timestamp)} />
                        <InfoRow label="Location" value={item.location} />
                        {item.topCandidate ? (
                          <InfoRow label="Top Candidate" value={item.topCandidate} isHighlight />
                        ) : null}
                        {item.hiringManager ? (
                          <InfoRow label="Hiring Manager" value={item.hiringManager} isHighlight />
                        ) : null}
                        {item.recruiter ? (
                          <InfoRow
                            label="Recruiter"
                            value={item.recruiter}
                            isHighlight={!item.hiringManager}
                          />
                        ) : null}
                        <InfoRow label="Contact Info" value={item.contactInfo} isLink />
                        {item.opportunityValue != null ? (
                          <InfoRow
                            label="Opportunity Value"
                            value={`$${Number(item.opportunityValue).toLocaleString()}`}
                          />
                        ) : null}
                        <InfoRow label="Lead Status" value={item.status} isHighlight />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 14, color: MUTED }}>
                No recent jobs or leads found for this member.
              </Typography>
            )}
          </Box>
        </Collapse>
      </Box>

      <Menu
        anchorEl={filterAnchorEl}
        open={filterOpen}
        onClose={() => setFilterAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            minWidth: 190,
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
          },
        }}
      >
        {CONTACT_FILTER_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={contactFilter === option.value}
            onClick={() => {
              setContactActionError("");
              setContactFilter(option.value);
              setFilterAnchorEl(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      <Dialog
        open={contactDialogOpen}
        onClose={closeContactDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <DialogTitle data-popup-header sx={{ pb: 1 }}>
          <Typography data-popup-title sx={{ fontSize: 18, fontWeight: 700, color: TEXT }}>
            {contactDialogMode === "edit" ? "Edit Contact" : "Add Contact"}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: "18px !important" }}>
          <Stack spacing={1.5}>
            {contactDialogError ? (
              <Typography sx={{ fontSize: 12, color: "#B45309" }}>
                {contactDialogError}
              </Typography>
            ) : null}

            {contactDialogMode === "create" && contactDialogEntryMode === "link" ? (
              <InlineLinkEntryCard
                title="Use Link"
                subtitle="Paste a public LinkedIn profile"
                value={contactLinkInput}
                onChange={handleContactLinkChange}
                onSubmit={handleApplyContactLink}
                buttonText="Apply Link"
                helperText={contactLinkSummary}
                errorText={contactLinkError}
                disabled={contactSaving}
              />
            ) : null}

            <TextField
              label="Contact Name"
              value={contactForm.name}
              onChange={handleContactFormChange("name")}
              size="small"
              fullWidth
              required
            />

            <TextField
              select
              label="Contact Type"
              value={contactForm.contact_kind}
              onChange={handleContactFormChange("contact_kind")}
              size="small"
              fullWidth
            >
              {CONTACT_KIND_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Role"
              value={contactForm.role}
              onChange={handleContactFormChange("role")}
              size="small"
              fullWidth
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Email"
                value={contactForm.email}
                onChange={handleContactFormChange("email")}
                size="small"
                fullWidth
              />
              <TextField
                label="Phone"
                value={contactForm.phone}
                onChange={handleContactFormChange("phone")}
                size="small"
                fullWidth
              />
            </Stack>

            <TextField
              label="LinkedIn URL"
              value={contactForm.linkedin_url}
              onChange={handleContactFormChange("linkedin_url")}
              size="small"
              fullWidth
            />

            <TextField
              label="Avatar URL"
              value={contactForm.profile_image}
              onChange={handleContactFormChange("profile_image")}
              size="small"
              fullWidth
            />

            <TextField
              label="Source"
              value={contactForm.source}
              onChange={handleContactFormChange("source")}
              size="small"
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions data-popup-footer sx={{ px: 3, py: 2 }}>
          <Button onClick={closeContactDialog} disabled={contactSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            className={withPillButtonClassName()}
            onClick={handleSaveContact}
            disabled={contactSaving}
            sx={[
              pillButtonInteractiveSx,
              pillButtonFilledSx,
              {
                px: 2.4,
                boxShadow: "none",
              },
            ]}
          >
            {contactSaving
              ? contactDialogMode === "edit"
                ? "Saving..."
                : "Adding..."
              : contactDialogMode === "edit"
              ? "Save Changes"
              : "Add Contact"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
